/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type VideoResponseModel } from '@/api/types/products-types';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import environmentConfig from '@/config/environment';

type Options = {
  skuId: string;
  videoLocation?: string;
  vehicleYear?: string;
  vehicleModel?: string;
  vehicleMake?: string;
  locale: Locale;
  subModelName?: string;
};

const selector = (data: VideoResponseModel[]) => data[0] ?? null;

export const getAcesSylvaniaVideos = async (options: Options, axiosInstance?: AxiosInstance) => {
  const { skuId, videoLocation, vehicleYear, vehicleModel, vehicleMake, locale, subModelName } =
    options;
  const country = getCountryFromLocale(locale);
  const salesChannel = 'ECOMM';
  const customerType = 'B2C';

  const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v2/sylvania-videos`;

  const params = {
    country,
    skuId,
    salesChannel,
    customerType,
    videoLocation,
    vehicleYear,
    vehicleModel,
    vehicleMake,
    subModelName,
  };

  const response = await getAxios(axiosInstance).get<VideoResponseModel[]>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

const {
  useData: useAcesSylvaniaVideosData,
  prefetch: prefetchAcesSylvaniaVideosFunc,
  query: getAcesSylvaniaVideosQuery,
} = createQuery<VideoResponseModel, Options>('getAcesSylvaniaVideos', getAcesSylvaniaVideos);

export const useAcesSylvaniaVideos = ({
  skuId,
  videoLocation,
  enabled = true,
}: {
  skuId: string;
  videoLocation?: string;
  vehicleYear?: number;
  vehicleModel?: string;
  vehicleMake?: string;
  subModelName?: string;
  enabled?: boolean;
}) => {
  const headerResult = useHeaderData();
  const locale = useLocale();
  const preferredVehicle = getPreferredVehicle(headerResult.data);
  const vehicleMake = preferredVehicle?.make;
  const subModelName = preferredVehicle?.subModel;
  const vehicleYear = preferredVehicle?.year;
  const vehicleModel = preferredVehicle?.model;

  const isVehicleSet = Boolean(vehicleMake && vehicleModel && vehicleYear && subModelName);

  return useAcesSylvaniaVideosData({
    enabled: headerResult.isSuccess && enabled && Boolean(skuId) && isVehicleSet,
    skuId,
    videoLocation,
    vehicleYear,
    vehicleModel,
    vehicleMake,
    locale,
    subModelName,
  });
};

export const getAcesSylvaniaVideosKey = ({
  skuId,
  locale,
  videoLocation,
  vehicleMake,
  vehicleModel,
  vehicleYear,
  subModelName,
}: Options) =>
  getAcesSylvaniaVideosQuery.getFullKey({
    skuId,
    videoLocation,
    vehicleYear,
    vehicleModel,
    vehicleMake,
    locale,
    subModelName,
  });

export const prefetchAcesSylvaniaVideos = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options,
) => {
  return prefetchAcesSylvaniaVideosFunc(queryClient, options, axiosInstance);
};
